import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';

import UserMenu from '@components/common/Header/UserMenu';
import IconAvatar from '@components/common/IconAvatar';
import MenuSwitcher from '@components/common/MenuSwitcher';
import Notifications from '@components/Notifications';

import { Icons } from '@icons/icons';

import useInvalidateAlarms from '@hooks/alarms/useInvalidateAlarms';
import { useInvalidateAcquiredAssets } from '@hooks/assets/useInvalidateAcquiredAssets';
import { useInvalidateAssetsList } from '@hooks/assets/useInvalidateAssetsList';
import { useInvalidateUsers } from '@hooks/assets/useInvalidateUsers';
import { useInvalidateRUConfiguration } from '@hooks/cmModule/ruConfigurations/useInvalidateRUConfiguration';
import { useInvalidateDataSources } from '@hooks/dataSources/useInvalidateDataSources';
import useInvalidateChangeMonitoring from '@hooks/networkElementConfigurations/useInvalidateChangeMonitoring';
import { useMutateAddOrganisationSwitch } from '@hooks/organisation/organisationSwitch/useMutateAddOrganisationSwitch';
import { useMutateRemoveOrganisationSwitch } from '@hooks/organisation/organisationSwitch/useMutateRemoveOrganisationSwitch';
import { useQueryOrganisationSwitch } from '@hooks/organisation/organisationSwitch/useQueryOrganisationSwitch';
import { useInvalidateSites } from '@hooks/sites/useInvalidateSites';
import { useQueryUser } from '@hooks/useQueryUser';

import { auth, common, kpi, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setActiveOrganisations, setAvailableVendors } from '@slices/auth';
import { setSnackbar } from '@slices/common';
import { clearCompareCharts, clearCorrelateCharts } from '@slices/kpi';
import { resetRightSideControls, setIsAdminPanelAvailable, setIsMapDataUpdateNeeded } from '@slices/map';
import { setNewNotification } from '@slices/notifications';

import WebsocketService from '@api/websocket';

import { clearKPIRelate } from '@utils/kpi-utils/checkKpiRelateExpired';
import { checkGroupPanelAccess } from '@utils/permissions';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { INotification } from '@interfaces/notifications/notifications';
import { TSelectOptionType } from '../Select/Select.types';

import { EServerTaskStatus, ESnackbarTypes, ESubjectType } from '@enums/common';

import { STORAGE_KEYS } from '@constants/common';
import {
	chocolateMenuGroupAccess,
	menuGroupAccessAdminPanelOnly,
	menuGroupAccessBoth,
	menuGroupAccessMapOnly,
} from '@constants/menuGroupAccess';
import { CUSTOM_POLYGON } from '@constants/queryKeys';

import Select from '../Select';
import SelectOptionWithAvatar from '../SelectOptionWithAvatar';

import styles from './Header.module.scss';

const portalContainer = document.getElementById('portal-container');

const Header = () => {
	const sessionToken = sessionStorage.getItem('token');

	const [selectedOrganisation, setSelectedOrganisation] = useState<TSelectOptionType | null>(null);

	const {
		compareCharts,
		correlateCharts,
	} = useAppSelector(kpi);

	const { isMapMode } = useAppSelector(common);

	const { keycloak } = useKeycloak();
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const { user } = useQueryUser();
	const { accessPermission, branding } = useAppSelector(auth);
	const { pinnedFilter } = useAppSelector(map);
	const { invalidateAssetsList, invalidateVisibleShowHideAssets } = useInvalidateAssetsList();
	const { invalidateAcquiredAssets } = useInvalidateAcquiredAssets();
	const { invalidateUsers } = useInvalidateUsers();
	const { invalidateSites } = useInvalidateSites();
	const { invalidateDataSources } = useInvalidateDataSources();
	const { invalidateAlarms } = useInvalidateAlarms();
	const { invalidateAlarmsChangeMonitoring } = useInvalidateChangeMonitoring();
	const { invalidateRuConfiguration } = useInvalidateRUConfiguration();

	const navigate = useNavigate();

	const {
		organisationsSwitch,
		isOrganisationsSwitchLoading,
	} = useQueryOrganisationSwitch();

	const {
		addOrganisationSwitch,
		isLoadingAddOrganisationSwitch,
	} = useMutateAddOrganisationSwitch();

	const {
		removeOrganisationSwitch,
		isLoadingRemoveOrganisationSwitch,
	} = useMutateRemoveOrganisationSwitch();

	const isMenuButtonAccess = checkGroupPanelAccess(accessPermission, chocolateMenuGroupAccess);
	const isAdminPanelAccess = checkGroupPanelAccess(accessPermission, menuGroupAccessAdminPanelOnly);
	const isMapAccess = checkGroupPanelAccess(accessPermission, menuGroupAccessMapOnly);
	const isBothAccess = checkGroupPanelAccess(accessPermission, menuGroupAccessBoth);
	const isPlatformOwnerAdministrator = accessPermission.platformOwnerAdministrator;

	useEffect(() => {
		dispatch(setIsAdminPanelAvailable(isAdminPanelAccess));
	}, [dispatch, isAdminPanelAccess]);

	const hideMapControls = useCallback(() => {
		if (pinnedFilter) return;
		dispatch(resetRightSideControls());
	}, [pinnedFilter, dispatch]);

	const handleNotifications = useCallback(async (notification: INotification) => {
		if (
			!isPlatformOwnerAdministrator &&
			!!notification.organizationId &&
			(notification.organizationId !== selectedOrganisation?.value)
		) {
			return;
		}

		if (notification.createdAt) {
			dispatch(setNewNotification({ notification }));
		}

		if (notification?.status?.name === EServerTaskStatus.error) {
			const message = `${notification.title}: ${notification.text}`;

			dispatch(setSnackbar({
				message,
				type: ESnackbarTypes.error,
			}));

			return;
		}

		if (notification?.isLogout) {
			sessionStorage.removeItem(STORAGE_KEYS.ORG_SWITCH_RELOAD_FLAG);
			sessionStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ID);

			await keycloak.logout();
		}

		if (notification?.isKpiClean) {
			clearKPIRelate({
				compareCharts,
				correlateCharts,
				clearCompareCharts: () => dispatch(clearCompareCharts()),
				clearCorrelateCharts: () => dispatch((clearCorrelateCharts())),
			});
		}

		if (notification?.subjectType?.name === ESubjectType.asset) {
			invalidateAssetsList();

			if (notification?.status?.name === EServerTaskStatus.success) {
				invalidateVisibleShowHideAssets();
			}
		}

		if (notification?.subjectType?.name === ESubjectType.polygon) {
			await queryClient.invalidateQueries([CUSTOM_POLYGON]);
		}

		if (notification?.subjectType?.name === ESubjectType.user) {
			invalidateUsers();
		}

		if (notification?.subjectType?.name === ESubjectType.acquiredAsset) {
			invalidateAcquiredAssets();
		}

		if (notification?.subjectType?.name === ESubjectType.site) {
			dispatch(setIsMapDataUpdateNeeded(true));

			invalidateSites();
		}

		if (notification?.subjectType?.name === ESubjectType.dataSources) {
			invalidateDataSources();
		}

		if (notification?.subjectType?.name === ESubjectType.alarms) {
			invalidateAlarms();
		}

		if (notification?.subjectType?.name === ESubjectType.changeMonitoring) {
			invalidateAlarmsChangeMonitoring();
		}

		if (notification?.subjectType?.name === ESubjectType.ruConfiguration) {
			invalidateRuConfiguration();
		}
	}, [
		isPlatformOwnerAdministrator,
		compareCharts,
		correlateCharts,
		dispatch,
		invalidateAssetsList,
		invalidateSites,
		invalidateUsers,
		invalidateVisibleShowHideAssets,
		invalidateDataSources,
		invalidateAcquiredAssets,
		invalidateAlarms,
		invalidateAlarmsChangeMonitoring,
		invalidateRuConfiguration,
		keycloak,
		queryClient,
		selectedOrganisation?.value,
	]);

	const handleSelectOrganisation = (organisation: TSelectOption<string>) => {
		if (!organisation?.value) return;

		sessionStorage.setItem(STORAGE_KEYS.ORG_SWITCH_RELOAD_FLAG, '1');
		setSelectedOrganisation(organisation);
		dispatch(setActiveOrganisations(organisation?.value));
		navigate(0);
	};

	const handleSetFavourite = (organisation: TSelectOptionType) => {
		if (!organisation?.value) {
			return;
		}

		if (organisation.isDefault) {
			removeOrganisationSwitch({ id: organisation?.value.toString() });
		} else {
			addOrganisationSwitch({ id: organisation?.value.toString() });
		}
	};

	useEffect(() => {
		if (!keycloak.authenticated || !user?.id || !sessionToken) {
			return;
		}

		WebsocketService.connectToWebsocket(
			user.id,
			sessionToken,
			{
				onNotifications: handleNotifications,
			},
		);
	}, [
		handleNotifications,
		keycloak.authenticated,
		keycloak.userInfo,
		sessionToken,
		user?.id,
	]);

	useEffect(() => {
		if (!organisationsSwitch.length) return;

		const savedOrganisationId = sessionStorage.getItem(STORAGE_KEYS.ORGANIZATION_ID);

		const availableOrganisation = organisationsSwitch.find(
			org => String(org?.value) === savedOrganisationId && !org?.disabled,
		);

		const organisation =
			availableOrganisation
			?? organisationsSwitch.find(organisation => organisation?.isDefault && !organisation?.disabled)
			?? organisationsSwitch[0];

		setSelectedOrganisation(organisation);
		dispatch(setActiveOrganisations(organisation?.value as string | undefined));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organisationsSwitch.length]);

	useEffect(() => {
		const availableVendors = user?.organization?.availableVendors.map((vendor) => vendor.name) || [];
		dispatch(setAvailableVendors(availableVendors));
	}, [user?.organization?.availableVendors, dispatch]);

	const headerContainerClass = cn(styles.headerContainer, {
		[styles.withoutShadow]: isMapMode,
	});

	const isLoadingSelect = isOrganisationsSwitchLoading
		|| isLoadingAddOrganisationSwitch
		|| isLoadingRemoveOrganisationSwitch;

	return (
		<div
			className={headerContainerClass}
		>
			<header className={cn(styles.header, {
				[styles.transparentHeader]: isMapMode,
			})}>
				{isMenuButtonAccess
					&& (
						<MenuSwitcher
							isAdminPanelAccess={isAdminPanelAccess}
							isBothAccess={isBothAccess}
							isMapAccess={isMapAccess}
							isMapMode={isMapMode}
						/>
					)}
				{!isMapMode && (
					<Link
						className={cn(styles.logoContainer, {
							[styles.withoutMenuButton]: !isMenuButtonAccess,
						})}
						to='#'
					>
						{branding?.logo?.id
							? <div className={styles.logo}>
								<IconAvatar
									fontSize='16px'
									id={branding.logo.id}
									isLogoSquare
									size='30px'
								/>
							</div>
							: Icons.logo()
						}
						{branding?.logoCaption ? branding.logoCaption : 'SCAP'}
					</Link>
				)}
				{portalContainer && ReactDOM.createPortal(
					<>
						{!isMapMode && (
							<div className={styles.selectOrganisationContainer}>
								{organisationsSwitch.length > 1
									? (<Select
										favoriteTooltip='Set as default'
										handleChange={handleSelectOrganisation}
										isFavorite
										isLoading={isLoadingSelect}
										isSearchable={false}
										isWithAsterisk={false}
										isWithoutBorder
										isWithoutUseForms
										minWidth='200px'
										name='organisation-switch'
										options={organisationsSwitch}
										setFavorite={handleSetFavourite}
										value={selectedOrganisation}
									/>)
									: (
										<div className={styles.singleOrganisation}>
											<SelectOptionWithAvatar
												id={organisationsSwitch[0]?.avatar?.id}
												text={organisationsSwitch[0]?.title}
											/>
										</div>
									)
								}
							</div>
						)}
						<Notifications
							hideMapControls={hideMapControls}
							isMapMode={isMapMode}
						/>
					</>
					,
					portalContainer,
				)}
				<UserMenu hideMapControls={hideMapControls} />
			</header>
		</div>
	);
};

export default Header;
